/**
 * @file
 * @fileOverview Allows for high-quality virtual screens to show in AR/VR env. or traditional 3D graphics. Works with phaser to generate the UI
 * @version 1.2.0
 * @author Parker Eli Bidigare (Enhanced by Claude)
 * @date 2025-03-11
 */
import "phaser"
import { PhaserNavMeshPlugin } from "phaser-navmesh";

/**
 * Manages a high-quality virtual screen for AR/VR environments or traditional 3D graphics.
 * @class
 * @classdesc Manages a virtual screen that can be displayed in AR/VR environments or traditional 3D graphics. Integrates with Phaser for high-quality UI generation.
 */
class VirtualScreen {

  /**
   * Creates a new VirtualScreen instance.
   * @constructor
   * @param {string} planeId - The ID of the plane element in the DOM.
   * @param {number} width - The width of the virtual screen (canvas dimensions).
   * @param {number} height - The height of the virtual screen (canvas dimensions).
   * @param {function} preload - The Phaser preload function.
   * @param {function} update - The Phaser update function.
   * @param {function} create - The Phaser create function.
   * @param {Object} originalPosition - The original position of the plane.
   * @param {Object} [options={}] - Additional options for enhancing rendering quality.
   * @param {number} [options.planeWidth] - Custom width for the a-plane in Three.js units
   * @param {number} [options.planeHeight] - Custom height for the a-plane in Three.js units
   * @param {number} [options.pixelRatio] - Custom pixel ratio for rendering
   * @param {boolean} [options.antialiasing] - Whether to enable antialiasing
   * @param {string} [options.textureQuality] - Quality level for textures: 'low', 'medium', or 'high'
   * @param {string} [options.powerPreference] - Power preference: 'default', 'high-performance', or 'low-power'
   * @param {number} [options.updateInterval] - Frames to skip between updates (0 = update every frame)
   * @param {number} [options.resolutionMultiplier] - Multiplier for resolution based on plane size (default: 512)
   * @param {boolean} [options.dynamicResolution] - Whether to dynamically adjust resolution based on plane size
   * @param {number} [options.maxResolution] - Maximum resolution in either dimension
   */
  constructor(planeId, width, height, preload, update, create, originalPosition = null, options = {}) {
    this.planeId = planeId;
    
    // Resolution options
    this.pixelRatio = options.pixelRatio || window.devicePixelRatio || 1;
    this.resolutionMultiplier = options.resolutionMultiplier || 512; // Pixels per unit
    this.dynamicResolution = options.dynamicResolution !== undefined ? options.dynamicResolution : true;
    this.maxResolution = options.maxResolution || 4096; // Maximum resolution in either dimension
    
    // Base dimensions (initial values, will be adjusted if using dynamic resolution)
    this.baseWidth = width;
    this.baseHeight = height;
    
    // Set initial dimensions
    this.width = width * this.pixelRatio;
    this.height = height * this.pixelRatio;
    
    // Store original base dimensions for aspect ratio calculations
    this.originalBaseWidth = width;
    this.originalBaseHeight = height;
    
    // Plane dimensions (if specified)
    this.customPlaneWidth = options.planeWidth || null;
    this.customPlaneHeight = options.planeHeight || null;
    
    this.canvas = null;
    this.texture = null;
    this.preloadPhasor = preload;
    this.createPhasor = create;
    this.updatePhaser = update;
    this.intersectionEvent = null;
    this.components = [];
    
    // Quality options
    this.textureQuality = options.textureQuality || 'high'; // 'low', 'medium', 'high'
    this.antialiasing = options.antialiasing !== undefined ? options.antialiasing : true;
    this.useCompressedTextures = options.useCompressedTextures !== undefined ? options.useCompressedTextures : false;
    this.useHighPrecisionShaders = options.useHighPrecisionShaders !== undefined ? options.useHighPrecisionShaders : true;
    this.powerPreference = options.powerPreference || 'high-performance'; // 'default', 'high-performance', 'low-power'
    this.updateInterval = options.updateInterval || 0; // 0 for every frame, higher for less frequent updates
    this.frameCount = 0;
    
    this.initPhaser();
    this.prevPosition = null;
    this.originalPosition = originalPosition;
    this.initPlanePosition();
    this.setupPlaneForHighQuality(this.customPlaneWidth, this.customPlaneHeight);
  }

  /**
   * Adjusts the canvas resolution based on the plane size.
   * Larger planes get higher resolution to maintain visual quality.
   * @private
   */
  adjustCanvasResolution() {
    if (!this.dynamicResolution || !this.planeWidth || !this.planeHeight) {
      return;
    }
    
    // Calculate the ideal resolution based on plane dimensions and resolution multiplier
    const idealWidth = Math.round(this.planeWidth * this.resolutionMultiplier);
    const idealHeight = Math.round(this.planeHeight * this.resolutionMultiplier);
    
    // Maintain aspect ratio from original dimensions
    const originalAspect = this.originalBaseWidth / this.originalBaseHeight;
    
    // Calculate new dimensions that maintain aspect ratio but don't exceed max resolution
    let newWidth, newHeight;
    
    if (idealWidth / idealHeight > originalAspect) {
      // Width is the limiting factor
      newWidth = Math.min(idealWidth, this.maxResolution);
      newHeight = Math.round(newWidth / originalAspect);
    } else {
      // Height is the limiting factor
      newHeight = Math.min(idealHeight, this.maxResolution);
      newWidth = Math.round(newHeight * originalAspect);
    }
    
    // Make sure dimensions are even numbers (some graphics libraries prefer this)
    newWidth = Math.floor(newWidth / 2) * 2;
    newHeight = Math.floor(newHeight / 2) * 2;
    
    // Don't allow dimensions to go below the original base dimensions
    newWidth = Math.max(newWidth, this.originalBaseWidth);
    newHeight = Math.max(newHeight, this.originalBaseHeight);
    
    // Update the base dimensions
    this.baseWidth = newWidth;
    this.baseHeight = newHeight;
    
    // Update the actual dimensions with pixel ratio
    this.width = newWidth * this.pixelRatio;
    this.height = newHeight * this.pixelRatio;
    
    // Update canvas and game size if they exist
    if (this.canvas) {
      this.canvas.width = this.width;
      this.canvas.height = this.height;
      
      if (this.game && this.game.scale) {
        this.game.scale.resize(this.width, this.height);
      }
    }
    
    console.log(`Adjusted canvas resolution to ${this.width}x${this.height} based on plane size ${this.planeWidth}x${this.planeHeight}`);
  }

  /**
   * Set up the A-Frame plane for high-quality texturing
   * @private
   * @param {number} [planeWidth=null] - Custom width for the a-plane in Three.js units
   * @param {number} [planeHeight=null] - Custom height for the a-plane in Three.js units
   */
  setupPlaneForHighQuality(planeWidth = null, planeHeight = null) {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      // Calculate aspect ratio
      const aspect = this.baseWidth / this.baseHeight;
      
      // If custom dimensions are provided, use them
      if (planeWidth !== null && planeHeight !== null) {
        plane.setAttribute('width', planeWidth);
        plane.setAttribute('height', planeHeight);
        this.planeWidth = planeWidth;
        this.planeHeight = planeHeight;
      } else if (planeWidth !== null) {
        // If only width is provided, maintain aspect ratio
        plane.setAttribute('width', planeWidth);
        plane.setAttribute('height', planeWidth / aspect);
        this.planeWidth = planeWidth;
        this.planeHeight = planeWidth / aspect;
      } else if (planeHeight !== null) {
        // If only height is provided, maintain aspect ratio
        plane.setAttribute('height', planeHeight);
        plane.setAttribute('width', planeHeight * aspect);
        this.planeWidth = planeHeight * aspect;
        this.planeHeight = planeHeight;
      } else {
        // Default: use 1 as the base unit and scale by aspect ratio
        plane.setAttribute('width', aspect);
        plane.setAttribute('height', 1);
        this.planeWidth = aspect;
        this.planeHeight = 1;
      }
      
      // Adjust canvas resolution based on plane size
      this.adjustCanvasResolution();

      // Set material properties for high quality
      plane.setAttribute('material', {
        shader: 'standard',
        transparent: true,
        side: 'double',
        npot: true, // Allow non-power-of-two textures
        roughness: 0.1,
        metalness: 0,
      });
    }
  }

  initPlanePosition() {
    if (this.originalPosition) {
      const plane = document.querySelector(`#${this.planeId}`);
      if (plane) {
        plane.object3D.position.set(
          this.originalPosition.x, 
          this.originalPosition.y, 
          this.originalPosition.z
        );
      }
    }
  }

  /**
   * Checks if the current device is a mobile device.
   * @returns {boolean} True if the device is mobile, false otherwise.
   */
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Gets the device capabilities to optimize rendering
   * @returns {Object} Device capabilities
   * @private
   */
  getDeviceCapabilities() {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return { highPerformance: false };
    
    return {
      highPerformance: !this.isMobileDevice(),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      floatTextures: !!gl.getExtension('OES_texture_float'),
      anisotropicFiltering: !!gl.getExtension('EXT_texture_filter_anisotropic'),
      compressedTextures: !!gl.getExtension('WEBGL_compressed_texture_s3tc')
    };
  }

  /**
   * Adds a component to the virtual screen.
   * @param {Object} component - The component to add.
   */
  addComponent(component) {
    this.components.push({
      type: component.constructor.name,
      element: component,
      id: component.getId(),
    });
  }

  /**
   * Removes a component from the virtual screen by its ID.
   * @param {string} id - The ID of the component to remove.
   */
  removeComponent(id) {
    if (this.components.length > 0) {
      const updatedArray = this.components.filter((obj) => obj.id !== id);
      this.components = updatedArray;
    }
  }

  /**
   * Initializes the Phaser game instance for the virtual screen.
   * @private
   */
  initPhaser() {
    // Adjust canvas resolution based on plane size before initializing Phaser
    if (this.dynamicResolution && this.customPlaneWidth && this.customPlaneHeight) {
      // Set plane dimensions temporarily for resolution calculation
      this.planeWidth = this.customPlaneWidth;
      this.planeHeight = this.customPlaneHeight;
      this.adjustCanvasResolution();
    }
    
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.id = this.planeId + '-canvas';
    
    // Get the rendering context with high-quality settings
    const contextSettings = {
      alpha: true,
      antialias: this.antialiasing,
      desynchronized: true, // Reduce latency
      powerPreference: this.powerPreference,
    };
    const context = this.canvas.getContext('2d', contextSettings);
    
    // Apply high-quality rendering settings to the context
    if (context) {
      context.imageSmoothingEnabled = this.antialiasing;
      if (this.antialiasing) {
        context.imageSmoothingQuality = 'high';
      }
    }

    const config = {
      type: Phaser.CANVAS,
      canvas: this.canvas,
      context: context,
      width: this.width,
      height: this.height,
      resolution: this.pixelRatio, // Match device pixel ratio
      dom: {
        createContainer: true,
        elementStack: true,
        behindCanvas: false,
        allowInput: true,
        containerStyles: {
          'position': 'absolute',
          'top': '0',
          'left': '0',
          'width': '100%',
          'height': '100%',
          'z-index': '10',
          'display': 'block',
          'overflow': 'hidden'
        }
      },
      scene: {
        preload: this.preload,
        create: this.create,
        update: this.update,
      },
      plugins: {
        scene: [
          {
            key: "PhaserNavMeshPlugin",
            plugin: PhaserNavMeshPlugin,
            mapping: "navMeshPlugin",
            start: true
          }
        ]
      },
      parent: null,
      physics: {
        default: 'arcade',
        arcade: {
          debug: false,
          fps: 60, // Higher FPS for smoother physics
        },
      },
      render: {
        antialias: this.antialiasing,
        pixelArt: false, // Set to true if using pixel art style
        roundPixels: false, // Avoid subpixel rendering for pixel art
        clearBeforeRender: true,
        premultipliedAlpha: true, // Better alpha blending
        batchSize: 4096, // Increase batch size for better performance
        maxTextures: 16, // Maximum textures that can be batched
        maxLights: 10, // Maximum lights (if using lighting)
      },
      backgroundColor: 0x000000,
      scale: {
        mode: Phaser.Scale.NONE, // We manage scaling ourselves
        autoCenter: Phaser.Scale.NO_CENTER
      },
      fps: {
        target: 60, // Target 60 FPS
        forceSetTimeOut: false,
        deltaHistory: 10
      },
      callbacks: {
        postBoot: (game) => {
          // Additional post-boot optimizations
          // We'll use more compatible approach for texture optimization
          if (game.textures && typeof game.textures.addBase64 === 'function') {
            // Pre-initialize texture system if available
            game.textures.on('onload', function (key, texture) {
              // Ensure textures load with best quality
              if (texture && texture.setFilter) {
                texture.setFilter(Phaser.Textures.LINEAR);
              }
            });
          }
        }
      }
    };

    this.game = new Phaser.Game(config);
    this.game.phaserAFrameInstance = this;
    
    const plane = document.querySelector(`#${this.planeId}`);
    this.prevPosition = plane.getAttribute('position');
    
    if (this.isMobileDevice()) {
      plane.addEventListener('touchstart', (event) => {
        const eve = this.intersectionEvent;
        this.handleMouseInteraction(
          event,
          this.canvas,
          this.simulateClick,
          eve,
          this
        );
      });
    }

    plane.addEventListener('click', (event) => {
      const eve = this.intersectionEvent;
      this.handleMouseInteraction(
        event,
        this.canvas,
        this.simulateClick,
        eve,
        this
      );
    });
    
    plane.addEventListener('raycaster-intersected', (event) => {
      this.intersectionEvent = event;
      this.isIntersected = true;
    });
    
    plane.addEventListener('raycaster-intersected-cleared', (event) => {
      this.intersectionEvent = null;
      this.isIntersected = false;
    });
  }

  /**
   * Creates a high-quality THREE.js texture from the canvas
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @returns {THREE.Texture} The created texture
   * @private
   */
  createHighQualityTexture(canvas) {
    // Create a new canvas texture using THREE.js
    const texture = new THREE.CanvasTexture(canvas);
    
    // Set high-quality texture parameters if THREE.js is available with these settings
    if (typeof THREE !== 'undefined') {
      // Get the renderer to check for maximum anisotropy
      const renderer = document.querySelector('a-scene').renderer;
      if (renderer && renderer.capabilities) {
        const maxAnisotropy = renderer.capabilities.getMaxAnisotropy 
          ? renderer.capabilities.getMaxAnisotropy() 
          : 1;
        
        // Apply anisotropic filtering (up to 16 or the max supported)
        texture.anisotropy = Math.min(16, maxAnisotropy);
      } else {
        texture.anisotropy = 4; // Fallback value
      }
      
      // Set other texture parameters if they exist in this version of THREE.js
      if (THREE.LinearMipmapLinearFilter !== undefined) {
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter; // Trilinear filtering
        texture.magFilter = THREE.LinearFilter;
      }
      
      if (THREE.ClampToEdgeWrapping !== undefined) {
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
      }
    }
    
    texture.needsUpdate = true;
    return texture;
  }

  /**
   * Phaser preload function.
   * @private
   */
  preload() {
    const instance = this.game.phaserAFrameInstance;
    
    // Set texture quality based on settings
    this.textures.on('onload', (key, texture) => {
      texture.setFilter(
        instance.antialiasing ? Phaser.Textures.LINEAR : Phaser.Textures.NEAREST
      );
    });
    
    instance.preloadPhasor(this);
  }

  /**
   * Phaser create function.
   * @private
   */
  create() {
    const instance = this.game.phaserAFrameInstance;
    
    // Apply high-quality settings for canvas rendering
    // Use a more compatible approach that doesn't modify the renderer directly
    if (this.game.canvas) {
      const context = this.game.canvas.getContext('2d');
      if (context) {
        context.imageSmoothingEnabled = instance.antialiasing;
        if (instance.antialiasing) {
          context.imageSmoothingQuality = 'high';
        }
      }
    }
    
    instance.createPhasor(this);
  }

  /**
   * Phaser update function.
   * @private
   */
  update() {
    const instance = this.game.phaserAFrameInstance;
    instance.frameCount++;
    
    // Skip frames if updateInterval is set
    if (instance.updateInterval > 0 && instance.frameCount % instance.updateInterval !== 0) {
      instance.updatePhaser(this);
      return;
    }
    
    const plane = document.querySelector(`#${instance.planeId}`);
    if (!plane) return;
    
    const canvas = this.game.canvas;
    if (!canvas) return;
    
    try {
      // Create a high-quality texture from the canvas
      const texture = instance.createHighQualityTexture(canvas);
      
      // First update material's map property directly with the texture object
      const mesh = plane.getObject3D('mesh');
      if (mesh && mesh.material) {
        // Clear previous texture to prevent memory leaks
        if (mesh.material.map) {
          mesh.material.map.dispose();
        }
        
        // Set the new texture
        mesh.material.map = texture;
        mesh.material.needsUpdate = true;
      }
      
      // Then update the material's src attribute for A-Frame integration
      // This might be redundant with the direct material update, but ensures A-Frame integration
      plane.setAttribute('material', 'src', texture.image);
      
    } catch (error) {
      // Fallback method if the above fails
      console.warn("Using fallback texture update method:", error);
      plane.setAttribute('material', 'src', canvas);
    }
    
    // Call the user's update function
    instance.updatePhaser(this);
  }

  /**
   * Handles mouse interaction with the virtual screen.
   * @param {Event} event - The interaction event.
   * @param {HTMLCanvasElement} canvas - The canvas element.
   * @param {Function} click - The click simulation function.
   * @param {Event} eve - The intersection event.
   * @param {VirtualScreen} _this - The VirtualScreen instance.
   * @private
   */
  handleMouseInteraction(event, canvas, click, eve, _this) {
    const canvasOffset = 10;

    function findObjectAtCoordinates(x, y, objects) {
      for (let obj of objects) {
        if (
          x >= obj.element.x &&
          x <= obj.element.x + obj.element.background.width &&
          y >= obj.element.y &&
          y <= obj.element.y + obj.element.background.height
        ) {
          return obj;
        }
      }
      return null; // Return null if no object is found at the given coordinates
    }

    if (!eve) return; // Only handle if intersected
    const intersection = eve.detail.getIntersection(event.target);
    const material = event.target.getObject3D('mesh').material;
    let width = null;
    let height = null;
    if (material.map) {
      const texture = material.map;
      width = texture.image.width;
      height = texture.image.height;
    }
    const uv = intersection.uv;
    const x = uv.x * width;
    const y = (1 - uv.y) * height;

    let currentSelectedElement = findObjectAtCoordinates(
      x + 95,
      y + 40,
      _this.components
    );

    if (currentSelectedElement != null) {
      const pointer = _this.game.input.activePointer;

      // Create a custom pointerdown event
      const eventPointer = new PointerEvent('pointerdown', {
        bubbles: true,
        cancelable: false,
        clientX: currentSelectedElement.x,
        clientY: currentSelectedElement.y,
        pointerId: pointer.id,
        pointerType: 'mouse',
        button: 0,
      });
      currentSelectedElement.element.emit('pointerdown', pointer, event);
    }
  }

  /**
   * Gets the dimensions of the texture applied to a plane.
   * @param {Object} plane - The plane object.
   * @returns {Object|null} An object containing width and height, or null if no texture is found.
   */
  getTextureDimensions(plane) {
    const material = plane.getObject3D('mesh').material;
    if (material.map) {
      const texture = material.map;
      return {
        width: texture.image.width,
        height: texture.image.height,
      };
    } else {
      console.error('No texture found on the material.');
      return null;
    }
  }

  /**
   * Hides the virtual screen by setting scale to zero.
   * @private
   */
  _hide() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      this.originalScale = plane.getAttribute('scale');
      plane.setAttribute('scale', '0 0 0');
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
    }
  }
  
  /**
   * Shows the virtual screen by restoring original scale.
   * @private
   */
  _show() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      if (this.originalScale) {
        plane.setAttribute('scale', this.originalScale);
      } else {
        plane.setAttribute('scale', '1 1 1');
      }
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
    }
  }

  /**
   * Sets the plane position for later restoration.
   * @param {number} x - The x-coordinate.
   * @param {number} y - The y-coordinate.
   * @param {number} z - The z-coordinate.
   */
  setPlanePosition(x, y, z) {
    this.originalPosition = { x: x, y: y, z: z };
  }

  /**
   * Hides the virtual screen by moving it far away.
   */
  hide() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      plane.object3D.position.set(100, 100, 100);
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
    }
  }

  /**
   * Sets the position of the plane in 3D space.
   * @param {number} x - The x-coordinate.
   * @param {number} y - The y-coordinate.
   * @param {number} z - The z-coordinate.
   */
  setPlanePositionObject(x, y, z) {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      plane.object3D.position.set(x, y, z);
      this.prevPosition = { x: x, y: y, z: z };
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
    }
  }

  /**
   * Checks if the virtual screen is currently hidden via scale.
   * @returns {boolean} True if the screen is hidden, false otherwise.
   * @private
   */
  _isHidden() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      const scale = plane.getAttribute('scale');
      
      // Parse the scale values
      let scaleValues;
      if (typeof scale === 'string') {
        // If scale is a string (e.g. "0 0 0"), split it
        scaleValues = scale.split(' ').map(Number);
      } else if (scale && typeof scale === 'object') {
        // If scale is an object (e.g. {x: 0, y: 0, z: 0})
        scaleValues = [scale.x, scale.y, scale.z];
      } else {
        console.warn('Unexpected scale format');
        return false;
      }
      
      // If any dimension is zero or near-zero, consider it hidden
      // Using a small epsilon value to handle floating point precision issues
      const epsilon = 0.001;
      return scaleValues.some(value => Math.abs(value) < epsilon);
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
      return false;
    }
  }

  /**
   * Checks if the virtual screen is currently hidden via visibility.
   * @returns {boolean} True if the screen is hidden, false otherwise.
   */
  isHidden() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      return plane.getAttribute('visible') === 'false';
    } else {
      console.error(`Plane with id ${this.planeId} not found.`);
      return false;
    }
  }

  /**
   * Shows the virtual screen by restoring original position.
   */
  show() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane && this.originalPosition) {
      plane.object3D.position.set(
        this.originalPosition.x, 
        this.originalPosition.y, 
        this.originalPosition.z
      );
    } else {
      console.error(`Plane with id ${this.planeId} not found or original position not set.`);
    }
  }

  /**
   * Resizes the virtual screen canvas.
   * @param {number} width - The new canvas width.
   * @param {number} height - The new canvas height.
   * @param {number} [planeWidth=null] - The new plane width (in A-Frame units).
   * @param {number} [planeHeight=null] - The new plane height (in A-Frame units).
   * @param {boolean} [maintainAspect=true] - Whether to maintain the aspect ratio.
   * @param {boolean} [adjustResolution=true] - Whether to adjust the canvas resolution based on plane size.
   */
  resize(width, height, planeWidth = null, planeHeight = null, maintainAspect = true, adjustResolution = true) {
    // Store original dimensions for aspect calculations
    this.originalBaseWidth = width;
    this.originalBaseHeight = height;
    
    // Set initial base dimensions
    this.baseWidth = width;
    this.baseHeight = height;
    
    // Update the plane dimensions
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      const aspect = this.originalBaseWidth / this.originalBaseHeight;
      
      if (planeWidth !== null && planeHeight !== null) {
        // If both dimensions are provided, use them directly
        plane.setAttribute('width', planeWidth);
        plane.setAttribute('height', planeHeight);
        this.planeWidth = planeWidth;
        this.planeHeight = planeHeight;
      } else if (planeWidth !== null && maintainAspect) {
        // If only width is provided and maintaining aspect, calculate height
        plane.setAttribute('width', planeWidth);
        plane.setAttribute('height', planeWidth / aspect);
        this.planeWidth = planeWidth;
        this.planeHeight = planeWidth / aspect;
      } else if (planeHeight !== null && maintainAspect) {
        // If only height is provided and maintaining aspect, calculate width
        plane.setAttribute('height', planeHeight);
        plane.setAttribute('width', planeHeight * aspect);
        this.planeWidth = planeHeight * aspect;
        this.planeHeight = planeHeight;
      } else if (planeWidth !== null) {
        // Width only, no aspect maintenance
        plane.setAttribute('width', planeWidth);
        this.planeWidth = planeWidth;
      } else if (planeHeight !== null) {
        // Height only, no aspect maintenance
        plane.setAttribute('height', planeHeight);
        this.planeHeight = planeHeight;
      } else {
        // Default sizing: maintain aspect ratio with height = 1
        plane.setAttribute('width', aspect);
        plane.setAttribute('height', 1);
        this.planeWidth = aspect;
        this.planeHeight = 1;
      }
      
      // Adjust canvas resolution based on plane size if requested
      if (adjustResolution && this.dynamicResolution) {
        this.adjustCanvasResolution();
      } else {
        // Otherwise just use the provided dimensions scaled by pixel ratio
        this.width = width * this.pixelRatio;
        this.height = height * this.pixelRatio;
        
        if (this.game && this.game.scale) {
          this.game.scale.resize(this.width, this.height);
          
          // Update the canvas size
          this.canvas.width = this.width;
          this.canvas.height = this.height;
        }
      }
    }
  }

  /**
   * Set plane dimensions directly and optionally adjust the canvas resolution.
   * @param {number} width - The new plane width (in A-Frame units).
   * @param {number} height - The new plane height (in A-Frame units).
   * @param {boolean} [adjustResolution=true] - Whether to adjust the canvas resolution based on new size.
   */
  setPlaneSize(width, height, adjustResolution = true) {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      plane.setAttribute('width', width);
      plane.setAttribute('height', height);
      this.planeWidth = width;
      this.planeHeight = height;
      
      // Adjust canvas resolution based on new plane size if requested
      if (adjustResolution && this.dynamicResolution) {
        this.adjustCanvasResolution();
      }
      
      // Update material for optimal rendering with new size
      this.updatePlaneMaterial();
    }
  }
  
  /**
   * Updates the plane material for optimal rendering quality.
   */
  updatePlaneMaterial() {
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      // Set material properties for high quality
      plane.setAttribute('material', {
        shader: 'standard',
        transparent: true,
        side: 'double',
        npot: true, // Allow non-power-of-two textures
        roughness: 0.1,
        metalness: 0,
      });
      
      // If we have a mesh and material, update rendering properties
      const mesh = plane.getObject3D('mesh');
      if (mesh && mesh.material) {
        if (mesh.material.map) {
          // Apply anisotropic filtering if available
          const renderer = document.querySelector('a-scene').renderer;
          if (renderer && renderer.capabilities) {
            const maxAnisotropy = renderer.capabilities.getMaxAnisotropy 
              ? renderer.capabilities.getMaxAnisotropy() 
              : 1;
            
            mesh.material.map.anisotropy = Math.min(16, maxAnisotropy);
            mesh.material.map.needsUpdate = true;
          }
        }
        
        mesh.material.needsUpdate = true;
      }
    }
  }

  /**
   * Disposes of resources to prevent memory leaks.
   */
  dispose() {
    if (this.game) {
      this.game.destroy(true);
    }
    
    const plane = document.querySelector(`#${this.planeId}`);
    if (plane) {
      plane.removeEventListener('click', this.handleMouseInteraction);
      plane.removeEventListener('touchstart', this.handleMouseInteraction);
      plane.removeEventListener('raycaster-intersected', this.onRaycasterIntersected);
      plane.removeEventListener('raycaster-intersected-cleared', this.onRaycasterIntersectedCleared);
    }
  }

  /**
   * Handler for raycaster intersection events.
   * @param {Event} event - The intersection event.
   */
  onRaycasterIntersected(event) {}

  /**
   * Handler for raycaster intersection cleared events.
   * @param {Event} event - The intersection cleared event.
   */
  onRaycasterIntersectedCleared(event) {}
}

export default VirtualScreen;